import { CheckSquare, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { displayName, newId, shortName } from '../../model/people';
import { sortTodos } from '../../model/research';
import { removeTodo, upsertTodo } from '../../model/tree';
import type { Id, Todo, TodoPriority, TodoStatus } from '../../model/types';
import { Field, Modal, PersonRow, Segmented } from '../components/common';
import { SuggestionsSection } from '../components/Suggestions';
import { PersonPicker } from '../components/PersonPicker';
import { TODO_KIND_SUGGESTIONS, TODO_PRIORITY_LABELS, TODO_STATUS_LABELS } from '../labels';
import { closeDialog, commit, confirmAction, getState, openDialog, showPerson, toast, useApp } from '../store';

/** Today as YYYY-MM-DD in the viewer's time zone. */
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function dueText(due: string): string {
  const [y, m, d] = due.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const days = Math.round((date.getTime() - new Date(new Date().toDateString()).getTime()) / 86400000);
  const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: y === new Date().getFullYear() ? undefined : 'numeric' });
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days < 0) return `Overdue (${label})`;
  return `Due ${label}`;
}

/** Mark a to-do done or not done. */
export function toggleTodo(td: Todo) {
  const done = td.status !== 'done';
  commit(upsertTodo(getState().tree!, { ...td, status: done ? 'done' : 'open', doneAt: done ? new Date().toISOString() : undefined }));
}

/** One to-do: a checkbox, its title, and what it's about. Clicking the row edits it. */
export function TodoRow({ todo: td, hidePerson }: { todo: Todo; hidePerson?: Id }) {
  const tree = useApp((s) => s.tree)!;
  const people = td.people.filter((id) => tree.people[id] && id !== hidePerson);
  const done = td.status === 'done';
  const overdue = !done && td.due && td.due < today();
  return (
    <div className={`todo-row${done ? ' done' : ''}`}>
      <input type="checkbox" checked={done} onChange={() => toggleTodo(td)} aria-label={done ? `Mark “${td.title}” not done` : `Mark “${td.title}” done`} />
      <button className="todo-body" onClick={() => openDialog({ type: 'edit-todo', todoId: td.id })}>
        <span className="title">{td.title}</span>
        <span className="meta">
          {td.priority === 'high' && !done && <span className="badge warn">High priority</span>}
          {td.status === 'in-progress' && <span className="badge accent">In progress</span>}
          {td.status === 'waiting' && <span className="badge">Waiting</span>}
          {[
            { key: 'kind', text: td.kind },
            // Marked overdue by what it is, not where it lands once empty parts are left out.
            { key: 'due', text: td.due && !done ? dueText(td.due) : '', overdue },
            { key: 'people', text: people.map((id) => shortName(tree.people[id])).join(', ') },
          ]
            .filter((x) => x.text)
            .map((x) => (
              <span key={x.key} className={x.overdue ? 'overdue' : undefined}>
                {x.text}
              </span>
            ))}
        </span>
      </button>
    </div>
  );
}

type Filter = 'open' | 'done' | 'all';

export function TodosView() {
  const tree = useApp((s) => s.tree)!;
  const [filter, setFilter] = useState<Filter>('open');
  const [person, setPerson] = useState<Id | null>(null);
  const all = useMemo(() => sortTodos(Object.values(tree.todos)), [tree]);
  const shown = all.filter((t) => (filter === 'all' ? true : filter === 'done' ? t.status === 'done' : t.status !== 'done')).filter((t) => !person || t.people.includes(person));
  const openCount = all.filter((t) => t.status !== 'done').length;

  return (
    <div className="page">
      <div className="page-inner todos-inner" style={{ maxWidth: 900 }}>
        <div className="page-head">
          <div>
            <h1 style={{ fontSize: 22 }}>To-dos & suggestions</h1>
            <p className="muted" style={{ marginTop: 4 }}>
              Research to do: records to find, facts to check, relatives to ask, and suggestions for what to add next. {openCount ? `${openCount} open.` : ''}
            </p>
          </div>
          <button className="btn primary" onClick={() => openDialog({ type: 'edit-todo', todoId: null, personIds: person ? [person] : undefined })}>
            <Plus size={15} /> New to-do
          </button>
        </div>
        <div className="row wrap">
          {all.length > 0 && <Segmented<Filter> ariaLabel="Show" value={filter} onChange={setFilter} options={[{ value: 'open', label: 'Open' }, { value: 'done', label: 'Done' }, { value: 'all', label: 'All' }]} />}
          <div style={{ flex: '1 1 240px', maxWidth: 360 }}>
            <PersonPicker tree={tree} value={person} onChange={setPerson} placeholder="About anyone" ariaLabel="Only to-dos and suggestions about" />
          </div>
        </div>
        <section className="report-section">
          <h2 className="section-heading">Your to-dos</h2>
          {all.length === 0 ? (
            <div className="card-box todos-empty">
              <CheckSquare size={18} aria-hidden />
              <span>
                <b>No to-dos yet.</b> <span className="muted">Keep track of records to order, facts to verify, and questions to ask, or add one of the suggestions below from its <MoreHorizontal size={13} aria-label="more" style={{ verticalAlign: '-2px' }} /> menu.</span>
              </span>
            </div>
          ) : shown.length === 0 ? (
            <p className="muted small-text" style={{ margin: 0 }}>{filter === 'open' ? (person ? 'Nothing left to do about them.' : 'Nothing left to do.') : 'Nothing matches.'}</p>
          ) : (
            <div className="card-box list-box">
              {shown.map((t) => (
                <TodoRow key={t.id} todo={t} />
              ))}
            </div>
          )}
        </section>
        <SuggestionsSection person={person} />
      </div>
    </div>
  );
}

export function TodoEditor({ todoId, personIds }: { todoId: Id | null; personIds?: Id[] }) {
  const tree = useApp((s) => s.tree)!;
  const existing = todoId ? tree.todos[todoId] : undefined;
  const [t, setT] = useState<Todo>(
    () => structuredClone(existing) ?? { id: newId('td'), title: '', status: 'open', priority: 'normal', people: personIds ?? [], createdAt: new Date().toISOString() },
  );
  const set = (patch: Partial<Todo>) => setT((x) => ({ ...x, ...patch }));
  if (todoId && !existing) {
    return (
      <Modal title="To-do not found" footer={<button className="btn" onClick={closeDialog}>Close</button>}>
        <p className="muted">This to-do has been deleted.</p>
      </Modal>
    );
  }
  const save = () => {
    if (!existing && !t.title.trim()) return toast('Say what needs doing. Everything else is optional.', 'error');
    const doneAt = t.status === 'done' ? t.doneAt ?? new Date().toISOString() : undefined;
    commit(upsertTodo(getState().tree!, { ...t, title: t.title.trim() || 'Untitled to-do', doneAt }));
    closeDialog();
  };
  return (
    <Modal
      title={existing ? 'Edit to-do' : 'New to-do'}
      footer={
        <>
          {existing && (
            <button
              className="btn danger left"
              onClick={() =>
                confirmAction({
                  title: 'Delete this to-do?',
                  body: `“${existing.title}” will be deleted.`,
                  confirmLabel: 'Delete',
                  danger: true,
                  onConfirm: () => {
                    commit(removeTodo(getState().tree!, existing.id));
                    closeDialog();
                  },
                })
              }
            >
              <Trash2 size={15} /> Delete
            </button>
          )}
          <button className="btn" onClick={closeDialog}>
            Cancel
          </button>
          <button className="btn primary" onClick={save}>
            Save
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="What to do">
          <input
            className="input"
            value={t.title}
            onChange={(e) => set({ title: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && save()}
            placeholder="e.g., Order Grandma’s birth certificate"
            autoFocus
          />
        </Field>
        <div className="grid-3">
          <Field label="Status">
            <select className="select" value={t.status} onChange={(e) => set({ status: e.target.value as TodoStatus })}>
              {Object.entries(TODO_STATUS_LABELS).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Priority">
            <select className="select" value={t.priority} onChange={(e) => set({ priority: e.target.value as TodoPriority })}>
              {Object.entries(TODO_PRIORITY_LABELS).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Due">
            <input className="input" type="date" value={t.due ?? ''} onChange={(e) => set({ due: e.target.value || undefined })} />
          </Field>
        </div>
        <Field label="Kind">
          <input className="input" value={t.kind ?? ''} list="todo-kinds" onChange={(e) => set({ kind: e.target.value || undefined })} placeholder="e.g., Verify" />
          <datalist id="todo-kinds">
            {TODO_KIND_SUGGESTIONS.map((k) => (
              <option key={k} value={k} />
            ))}
          </datalist>
        </Field>
        <Field label="Notes">
          <textarea className="textarea" style={{ minHeight: 60 }} value={t.notes ?? ''} onChange={(e) => set({ notes: e.target.value || undefined })} />
        </Field>
        <div className="section-title">About ({t.people.length})</div>
        <PersonPicker tree={tree} value={null} exclude={t.people} placeholder="Link someone: search by name…" ariaLabel="Link someone to this to-do" onChange={(id) => id && set({ people: [...t.people, id] })} />
        <div className="stack tight">
          {t.people
            .filter((id) => tree.people[id])
            .map((id) => (
              <PersonRow
                key={id}
                person={tree.people[id]}
                onClick={() => showPerson(id)}
                actions={
                  <button className="btn icon small ghost danger" aria-label={`Unlink ${displayName(tree.people[id])}`} onClick={() => set({ people: t.people.filter((x) => x !== id) })}>
                    <Trash2 size={14} />
                  </button>
                }
              />
            ))}
        </div>
      </div>
    </Modal>
  );
}

